import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Search, Filter, MoreHorizontal, MapPin, Calendar, Users, FileText,
  Eye, Edit, Trash2, BarChart3, Loader2,
} from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useNavigation } from '../hooks/useMe';

const STATUS_COLORS = {
  Active: 'bg-green-500',
  'In Progress': 'bg-blue-500',
  Planning: 'bg-yellow-500',
  Completed: 'bg-gray-500',
  'On Hold': 'bg-orange-500',
};

export function Projects({ onNavigateToAnalytics, onProjectSelect }) {
  const navigation = useNavigation();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');

  // Flatten the user's permitted Industry → Project tree into the row shape
  // the table expects. Real per-project status / progress / location / team
  // come from the navigation payload when present, otherwise the row leaves
  // those columns blank instead of fabricating values.
  const allProjects = useMemo(() => {
    const out = [];
    (navigation?.industries || []).forEach((ind) => {
      (ind.projects || []).forEach((p) => {
        out.push({
          id: p.code,
          rawId: p.id,
          name: p.name,
          industryType: ind.name,
          deliverables: (p.deliverables || []).map((d) => d.name).join(', '),
          location: p.location || '',
          status: p.status || '',
          progress: typeof p.progress === 'number' ? p.progress : 0,
          startDate: p.start_date || null,
          team: typeof p.team_size === 'number' ? p.team_size : (p.deliverables || []).length,
          industry: { id: ind.id, code: ind.code, name: ind.name, slug: ind.slug },
          deliverablesList: p.deliverables || [],
        });
      });
    });
    return out;
  }, [navigation]);

  const industries = useMemo(
    () => Array.from(new Set(allProjects.map((p) => p.industryType))).sort(),
    [allProjects],
  );

  const filteredProjects = useMemo(() => {
    return allProjects.filter((project) => {
      const needle = searchTerm.toLowerCase();
      const matchesSearch =
        !needle ||
        project.name.toLowerCase().includes(needle) ||
        project.id.toLowerCase().includes(needle) ||
        project.location.toLowerCase().includes(needle);
      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
      const matchesIndustry = industryFilter === 'all' || project.industryType === industryFilter;
      return matchesSearch && matchesStatus && matchesIndustry;
    });
  }, [allProjects, searchTerm, statusFilter, industryFilter]);

  if (!navigation) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading projects…
      </div>
    );
  }

  const handleRowClick = (project) => {
    onProjectSelect?.({
      id: project.rawId,
      title: project.name,
      industry: project.industry,
      deliverables: project.deliverablesList,
    });
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card className="border-border bg-card">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Planning">Planning</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {industries.map((i) => (
                  <SelectItem key={i} value={i}>{i}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Projects Table */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Projects ({filteredProjects.length})</span>
            <Button variant="outline" size="sm">
              <Filter className="w-4 h-4 mr-2" />
              Advanced Filters
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-muted/50">
                  <TableHead className="font-semibold">Project ID</TableHead>
                  <TableHead className="font-semibold">Project Name</TableHead>
                  <TableHead className="font-semibold">Industry</TableHead>
                  <TableHead className="font-semibold">Deliverables</TableHead>
                  <TableHead className="font-semibold">Location</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Progress</TableHead>
                  <TableHead className="font-semibold">Team</TableHead>
                  <TableHead className="font-semibold">Analytics</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground text-sm">
                      No projects match your filters.
                    </TableCell>
                  </TableRow>
                )}
                {filteredProjects.map((project) => (
                  <TableRow
                    key={project.rawId}
                    className="border-border hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleRowClick(project)}
                  >
                    <TableCell className="font-mono text-sm">{project.id}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium text-foreground">{project.name}</div>
                        {project.startDate && (
                          <div className="flex items-center text-xs text-muted-foreground">
                            <Calendar className="w-3 h-3 mr-1" />
                            Started {new Date(project.startDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-muted-foreground/20">
                        {project.industryType}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm max-w-[220px] truncate" title={project.deliverables}>
                      {project.deliverables}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm">
                        <MapPin className="w-3 h-3 mr-1 text-muted-foreground" />
                        {project.location}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${STATUS_COLORS[project.status] || 'bg-gray-500'}`}></div>
                        <span className="text-sm font-medium">{project.status}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm font-medium">{project.progress}%</div>
                        <div className="w-full bg-muted rounded-full h-1.5">
                          <div
                            className="bg-primary h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <Users className="w-3 h-3 text-muted-foreground" />
                        <span className="text-sm">{project.team}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onNavigateToAnalytics?.(); }}
                        className="hover:bg-blue-500/10 hover:text-blue-500 hover:border-blue-500"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleRowClick(project)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Project
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <FileText className="w-4 h-4 mr-2" />
                            Generate Report
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Project
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
